/**
 * OracleResult.jsx — Oracle 问卜结果卡片
 * Phase 98B / v2.2 (评分分解卡片)
 *
 * 显示:
 *  - 路由解释（为什么用这个引擎）
 *  - 引擎事实层（卦象/关键词/评分分解）
 *  - LLM 白话叙事
 *  - 化解建议（如有）
 *  - 追问输入框
 *  - PDF 上卖引导（可选）
 *  - 澄清选项（clarify 场景）
 */

import React, { useState, useRef, useEffect } from "react";
import { API } from "../constants/config";
import DharmaResultCard from "./DharmaResultCard";
import TermTooltip from "./TermTooltip";

const GD  = "#D4AF37";
const GDH = "#B8960C";
const GR  = "#064E3B";

// ─── 模块声明文案（结果页顶部来源行）─────────────────────────────────────────
// 标注原则：确定性计算→\"非AI编造\"；LLM参与叙事→\"AI解读/叙述\"
const MODULE_DECLARATION = {
  liuyao:  { zh: "此结果由【六爻起卦】推演得出", sub: "卦象已定 · AI解读" },
  bazi:    { zh: "此结果由【八字能量】推演得出", sub: "四柱已排 · AI解读" },
  ziwei:   { zh: "此结果由【紫微斗数】推演得出", sub: "命盘已排 · 星曜已定" },
  dream:   { zh: "此结果由【周公解梦】推演得出", sub: "259条词典匹配 · 非AI编造" },
  almanac: { zh: "此结果由【老黄历】推演得出",   sub: "实时排盘 · 非AI编造" },
  lottery: { zh: "此结果由【观音灵签】推演得出", sub: "100签对照 · 非AI编造" },
  naming:  { zh: "此结果由【五格剖象】推演得出", sub: "笔画数理 · 非AI编造" },
  dharma:  { zh: "此结果由【密法修行建议】推演得出", sub: "五根柱子框架 · AI叙述" },
};

// ─── 引用锚点生成 ────────────────────────────────────────────────────────────
function engineCitation(routed_module, engine_facts) {
  if (!engine_facts) return "";
  const ef = engine_facts;
  switch (routed_module) {
    case "liuyao": {
      const parts = [];
      if (ef.base_hexagram) parts.push(`卦象「${ef.base_hexagram}」`);
      if (ef.base_hexagram && ef.changed_hexagram && ef.changed_hexagram !== ef.base_hexagram) {
        parts[parts.length - 1] = `卦象「${ef.base_hexagram}」→「${ef.changed_hexagram}」`;
      }
      if (ef.dong_bian_type) parts.push(ef.dong_bian_type);
      if (ef.yongshen) parts.push(`用神${ef.yongshen}`);
      if (typeof ef.rigidity_score === "number") parts.push(`综合评分${ef.rigidity_score > 0 ? '+' : ''}${ef.rigidity_score}`);
      return parts.length ? `↳ 引擎依据 · ${parts.join(' · ')}` : "↳ 引擎依据 · 六爻起卦推演";
    }
    case "dream":
      return ef.keywords?.length ? `↳ 引擎依据 · 解梦关键词「${ef.keywords[0]}」` : "↳ 引擎依据 · 周公解梦词典匹配";
    case "almanac":
      return "↳ 引擎依据 · 老黄历实时排盘";
    case "lottery":
      return ef.lot ? `↳ 引擎依据 · 观音灵签第${ef.lot}签` : "↳ 引擎依据 · 观音灵签";
    case "dharma":
      return "↳ 引擎依据 · 密法五根柱子框架";
    case "naming":
      return "↳ 引擎依据 · 五格剖象+康熙笔画";
    default:
      return "↳ 引擎依据 · LUMINA能量引擎";
  }
}

// ─── 模块图标 ─────────────────────────────────────────────────────────────────
const MODULE_ICON = {
  liuyao:  "⚡",
  bazi:    "🔮",
  ziwei:   "🌟",
  dream:   "💭",
  almanac: "📅",
  lottery: "🙏",
  naming:  "✍️",
  clarify: "💡",
  dharma:  "☸️",
};

// ─── 评分颜色 / 标签 ──────────────────────────────────────────────────────────
function scoreColor(score) {
  if (score >= 60)  return "#059669";
  if (score >= 20)  return "#D97706";
  if (score >= -20) return "#64748B";
  if (score >= -60) return "#B45309";
  return "#DC2626";
}

function scoreLabel(score) {
  if (score >= 60)  return "较为顺利";
  if (score >= 30)  return "整体偏吉";
  if (score >= -10) return "平，需观察";
  if (score >= -40) return "中等偏下";
  if (score >= -70) return "明显不利";
  return "须高度谨慎";
}

function scorePlusMinus(n) {
  return n > 0 ? `+${n}` : `${n}`;
}

// ─── 解析 score_breakdown 字符串 → 结构化行 ──────────────────────────────────
// 输入格式: "六神发动: -60 (白虎+父母+旺)"  /  "世爻旺衰: +30 (旺)"
function parseBreakdown(str) {
  const m = str.match(/^(.+?):\s*([+-]?\d+)\s*(?:\((.+)\))?$/);
  if (!m) return null;
  return { label: m[1].trim(), score: parseInt(m[2], 10), detail: m[3] || "" };
}

const SIX_REL_ZH = { 父母: "文书", 官鬼: "压力", 妻财: "钱财", 子孙: "化解", 兄弟: "竞争" };
const SPIRIT_ZH  = { 白虎: "白虎", 朱雀: "朱雀", 青龙: "青龙", 玄武: "玄武", 腾蛇: "腾蛇", 勾陈: "勾陈" };
const DONG_BIAN_WHY = {
  "回头克": "动爻克用神，能量自耗",
  "化绝":   "变爻入绝地，事难成局",
  "化空":   "变爻落空亡，好事落空",
  "化进神": "变爻递进，后劲渐强",
  "化退神": "变爻递退，势头减弱",
  "回头生": "动爻生用神，有助力",
};

function annotate({ label, score, detail }) {
  // ── 六神发动 ──
  if (label.includes("六神")) {
    const parts = detail.split("+");
    const spirit  = parts[0] || "";
    const sixRel  = parts[1] || "";
    const wangxiu = parts[2] || "";
    const friendlyLabel = `${SPIRIT_ZH[spirit] || spirit}${SIX_REL_ZH[sixRel] || sixRel}`;
    const wangxiuWhy = { 旺: "当令旺盛", 相: "有气相扶", 休: "气息平稳", 囚: "受制稍弱", 死: "气已耗尽" };
    const why = `${spirit}×${SIX_REL_ZH[sixRel] || sixRel} · ${wangxiuWhy[wangxiu] || wangxiu}`;
    return { friendlyLabel, why };
  }

  // ── 动变生克 ──
  if (label.includes("动变")) {
    const why = DONG_BIAN_WHY[detail] || (score <= -40 ? "严重消耗，须谨慎" : score < 0 ? "有耗损" : score > 0 ? "助推力" : "平稳无冲");
    return { friendlyLabel: detail || "动变", why };
  }

  // ── 用神旺衰 ──
  if (label.includes("用神旺衰")) {
    const yong = detail.replace(/[旺相休囚死平不现]/g, "").trim() || "用神";
    const wx   = detail.slice(-1);
    const wxMap = { 旺: "目标能量充足", 相: "目标偏旺", 休: "目标气息平", 囚: "目标稍弱", 死: "目标能量耗尽", 平: "目标能量中立" };
    return { friendlyLabel: `用神${wx === "不" ? "不现" : wx}`, why: `${yong} · ${wxMap[wx] || wx}` };
  }

  // ── 用神分类 ──
  if (label.includes("用神分类")) {
    const why = score > 0 ? "引擎判定偏吉" : score < 0 ? "引擎判定偏凶" : "引擎判定中立";
    return { friendlyLabel: "用神质量", why };
  }

  // ── 世爻旺衰 ──
  if (label.includes("世爻")) {
    const wx = detail || "";
    const wxMap = { 旺: "你的状态很好", 相: "状态偏好", 休: "状态一般", 囚: "状态偏弱", 死: "状态低落" };
    return { friendlyLabel: `自身${wx}`, why: wxMap[wx] || "当下自身状态" };
  }

  // ── 忌神克用 ──
  if (label.includes("忌神")) {
    return { friendlyLabel: "忌神克用", why: detail || "不利因素克制目标" };
  }

  return { friendlyLabel: label, why: detail || "" };
}

// ─── 评分分解卡片组件 ─────────────────────────────────────────────────────────
function ScoreBreakdownCard({ breakdown, rigidityScore, keyWarning }) {
  if (!breakdown || breakdown.length === 0) return null;

  const rows = breakdown.map(s => {
    const parsed = parseBreakdown(s);
    if (!parsed) return null;
    const { friendlyLabel, why } = annotate(parsed);
    return { ...parsed, friendlyLabel, why };
  }).filter(Boolean);

  if (rows.length === 0) return null;

  return (
    <div style={{
      marginTop: 14, padding: "14px 16px",
      background: "rgba(6,78,59,.03)", borderRadius: 10,
      border: "1px solid rgba(212,175,55,.2)",
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: GR, marginBottom: 10, letterSpacing: 0.5 }}>
        📊 评分分析
      </div>

      {/* 明细行 */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {rows.map((row, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 8, alignItems: "center", fontSize: 13 }}>
            <span style={{ color: "#475569", fontWeight: 500 }}>{row.friendlyLabel}</span>
            <span style={{
              fontVariantNumeric: "tabular-nums", fontWeight: 700, textAlign: "right",
              color: scoreColor(row.score), minWidth: 36,
            }}>
              {scorePlusMinus(row.score)}
            </span>
            <span style={{ color: "#94A3B8", fontSize: 12 }}>{row.why}</span>
          </div>
        ))}
      </div>

      {/* 分隔线 + 综合行 */}
      <div style={{ borderTop: "1px solid rgba(212,175,55,.2)", margin: "10px 0" }} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 8, alignItems: "center", fontSize: 13 }}>
        <span style={{ color: GR, fontWeight: 700 }}>综合</span>
        <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 700, textAlign: "right", color: scoreColor(rigidityScore), minWidth: 36 }}>
          {scorePlusMinus(rigidityScore)}
        </span>
        <span style={{ color: scoreColor(rigidityScore), fontSize: 12, fontWeight: 600 }}>
          {scoreLabel(rigidityScore)}
        </span>
      </div>

      {/* 核心问题 */}
      {keyWarning && (
        <div style={{ marginTop: 10, fontSize: 12, color: "#64748B", paddingTop: 8, borderTop: "1px solid #F1F5F9" }}>
          <span style={{ fontWeight: 600, color: "#475569" }}>核心问题：</span>
          {keyWarning}
        </div>
      )}
    </div>
  );
}

// ─── 引用锚点行（含术语tooltip）────────────────────────────────────────────────
const DONG_BIAN_TERMS = new Set(["回头克", "化绝", "化空", "化进神", "化退神", "回头生"]);

function CitationWithTooltips({ routed_module, engine_facts }) {
  if (!engine_facts) return <>{engineCitation(routed_module, engine_facts)}</>;
  const ef = engine_facts;

  if (routed_module === "liuyao") {
    const parts = [];
    const base = ef.base_hexagram;
    const changed = ef.changed_hexagram || ef.changing_hexagram;
    if (base) {
      parts.push({ text: changed && changed !== base ? `卦象「${base}」→「${changed}」` : `卦象「${base}」` });
    }
    if (ef.dong_bian_type) {
      const dt = ef.dong_bian_type;
      parts.push({ term: dt, engine: "liuyao", canTooltip: DONG_BIAN_TERMS.has(dt) });
    }
    if (ef.yongshen) {
      parts.push({ term: "用神", engine: "liuyao", suffix: ef.yongshen, canTooltip: true });
    }
    if (typeof ef.rigidity_score === "number") {
      parts.push({ text: `综合评分${ef.rigidity_score > 0 ? "+" : ""}${ef.rigidity_score}` });
    }
    if (!parts.length) return <>↳ 引擎依据 · 六爻起卦推演</>;
    return (
      <>
        ↳ 引擎依据 ·{" "}
        {parts.map((p, i) => (
          <React.Fragment key={i}>
            {i > 0 && " · "}
            {p.term && p.canTooltip
              ? <><TermTooltip term={p.term} engine={p.engine}>{p.term}</TermTooltip>{p.suffix ? ` ${p.suffix}` : ""}</>
              : p.term
                ? <>{p.term}{p.suffix ? ` ${p.suffix}` : ""}</>
                : p.text
            }
          </React.Fragment>
        ))}
      </>
    );
  }

  // 其他模块：直接使用字符串
  return <>{engineCitation(routed_module, engine_facts)}</>;
}

// ─── 主组件 ──────────────────────────────────────────────────────────────────
export default function OracleResult({ result, originalQuery, onFollowUp, onNavigate, onNewQuestion }) {
  const [followInput, setFollowInput] = useState("");
  const [feedbackState, setFeedbackState] = useState(null); // null | "thumbs_up" | "thumbs_down"
  const followRef = useRef(null);

  // 结果出现后自动滚动到此处
  useEffect(() => {
    setTimeout(() => followRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  }, [result]);

  if (!result) return null;

  const {
    routed_module,
    routing_explanation: expl,
    engine_facts,
    narrative,
    remedies = [],
    need_birth_info,
    clarify_options,
    upsell,
    dharma_guidance,
    response_time_ms,
    query_id = "",
  } = result;

  const icon = MODULE_ICON[routed_module] || "🌀";
  const rigidity = engine_facts?.rigidity_score;
  const hexZh    = engine_facts?.base_hexagram;
  const changedZh = engine_facts?.changed_hexagram;
  const dongBian = engine_facts?.dong_bian_type;
  const yongshen = engine_facts?.yongshen;
  const keywords = engine_facts?.keywords;

  // ── 澄清场景 ──────────────────────────────────────────────────────────────
  if (routed_module === "clarify" || clarify_options) {
    const opts = (clarify_options || "").split("\n").filter(l => l.trim());
    return (
      <div ref={followRef} style={cardStyle}>
        <div style={headerStyle}>
          <span style={{ fontSize: 22 }}>💡</span>
          <div>
            <div style={moduleNameStyle}>澄清问题</div>
            <div style={explWhyStyle}>{expl?.why || "您的问题比较模糊，请选择方向"}</div>
          </div>
        </div>
        <div style={{ marginTop: 16 }}>
          {opts.map((opt, i) => (
            <div key={i} style={{ fontSize: 14, color: "#334155", padding: "8px 0", borderBottom: i < opts.length - 1 ? "1px solid #F1F5F9" : "none" }}>
              {opt}
            </div>
          ))}
        </div>
        <FollowUpInput value={followInput} onChange={setFollowInput} onSubmit={onFollowUp} />
      </div>
    );
  }

  // ── 八字引导（需要生辰）──────────────────────────────────────────────────
  if (need_birth_info) {
    return (
      <div ref={followRef} style={cardStyle}>
        <div style={headerStyle}>
          <span style={{ fontSize: 22 }}>📊</span>
          <div>
            <div style={moduleNameStyle}>八字+紫微分析</div>
            <div style={explWhyStyle}>{expl?.why}</div>
          </div>
        </div>
        <div style={{ marginTop: 16, fontSize: 14, color: "#475569", lineHeight: 1.7 }}>
          {narrative || "回答这个问题需要您的八字命盘。"}
        </div>
        <div style={{ marginTop: 16 }}>
          <button
            onClick={() => onNavigate("/audit")}
            style={{ background: GD, color: "#fff", border: "none", borderRadius: 8,
              padding: "10px 22px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
          >
            前往输入生辰 →
          </button>
        </div>
      </div>
    );
  }

  // ── 普通结果卡片 ──────────────────────────────────────────────────────────
  return (
    <div ref={followRef} style={{ ...cardStyle, animation: "slideUp .35s ease forwards" }}>
      <style>{`@keyframes slideUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}`}</style>

      {/* Close / new question */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <button onClick={() => onNewQuestion && onNewQuestion()} style={{
          background: "none", border: "1px solid #E2E8F0", borderRadius: 6,
          color: "#94A3B8", fontSize: 12, padding: "4px 10px", cursor: "pointer",
          fontFamily: "'Noto Sans SC','PingFang SC',sans-serif",
        }}>
          ✕ 新问题
        </button>
      </div>

      {/* 头部: 模块 + 路由解释 */}
      <div style={headerStyle}>
        <span style={{ fontSize: 24 }}>{icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={moduleNameStyle}>{expl?.module_zh || routed_module}</span>
            <span style={{ fontSize: 11, color: "#94A3B8" }}>
              置信度 {Math.round((expl?.confidence || 0) * 100)}%{response_time_ms > 0 ? ` · ${response_time_ms}ms` : ''}
            </span>
          </div>
          <div style={explWhyStyle}>{expl?.why}</div>
        </div>
      </div>

      {/* 来源声明行 */}
      {MODULE_DECLARATION[routed_module] && (
        <div style={{
          marginTop: 10, padding: "8px 12px",
          background: "rgba(6,78,59,.04)", borderRadius: 8,
          border: "1px solid rgba(212,175,55,.12)",
          display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
        }}>
          <span style={{ fontSize: 12, color: GR, fontWeight: 600 }}>
            {MODULE_DECLARATION[routed_module].zh}
          </span>
          <span style={{ fontSize: 11, color: "#94A3B8" }}>
            {routed_module === "liuyao" && engine_facts?.yongshen
              ? <>· <TermTooltip term="用神" engine="liuyao">用神</TermTooltip> {engine_facts.yongshen} · {MODULE_DECLARATION[routed_module].sub}</>
              : <>· {MODULE_DECLARATION[routed_module].sub}</>
            }
          </span>
        </div>
      )}

      {/* Phase 120: 卦象元数据 (质量/自代占/再占) — 仅六爻 */}
      {routed_module === "liuyao" && engine_facts && (
        (engine_facts.cast_mode && engine_facts.cast_mode !== "self") ||
        (engine_facts.cast_quality && engine_facts.cast_quality !== "good") ||
        engine_facts.is_recast
      ) && (
        <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
          {engine_facts.cast_mode && engine_facts.cast_mode !== "self" && (
            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, background: "rgba(107,114,128,0.08)", color: "#6B7280", fontWeight: 500 }}>
              匿名问事 · 信息有限
            </span>
          )}
          {engine_facts.cast_quality && engine_facts.cast_quality !== "good" && (
            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, background: engine_facts.cast_quality === "poor" ? "rgba(220,38,38,0.08)" : "rgba(217,119,6,0.08)", color: engine_facts.cast_quality === "poor" ? "#DC2626" : "#D97706", fontWeight: 500 }}>
              {engine_facts.cast_quality === "poor" ? "卦象不稳" : "信号偏弱"}
              {engine_facts.cast_quality_suggestion && ` — ${engine_facts.cast_quality_suggestion}`}
            </span>
          )}
          {engine_facts.is_recast && (
            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, background: "rgba(124,58,237,0.08)", color: "#7C3AED", fontWeight: 500 }}>
              再占 · {engine_facts.recast_note}
            </span>
          )}
        </div>
      )}

      {/* 六爻引擎事实层 — 卦名 + 用神徽章 */}
      {routed_module === "liuyao" && hexZh && (
        <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          <span style={factBadge}>{hexZh}</span>
          {changedZh && changedZh !== hexZh && (
            <>
              <span style={{ color: "#94A3B8", fontSize: 13 }}>→</span>
              <span style={{ ...factBadge, background: "rgba(212,175,55,.12)" }}>{changedZh}</span>
            </>
          )}
          {dongBian && <span style={{ ...factBadge, background: "#FEF2F2", color: "#B91C1C" }}>{dongBian}</span>}
          {yongshen && <span style={{ ...factBadge, background: "#F0FDF4", color: "#166534" }}>用神: {yongshen}</span>}
        </div>
      )}

      {/* 六爻评分分解卡片 */}
      {routed_module === "liuyao" && (
        <ScoreBreakdownCard
          breakdown={engine_facts?.score_breakdown}
          rigidityScore={typeof rigidity === "number" ? rigidity : 0}
          keyWarning={engine_facts?.key_warning}
        />
      )}

      {/* 解梦关键词 */}
      {routed_module === "dream" && keywords && keywords.length > 0 && (
        <div style={{ marginTop: 14, display: "flex", gap: 6, flexWrap: "wrap" }}>
          {keywords.map(kw => (
            <span key={kw} style={{ ...factBadge, background: "rgba(139,92,246,.1)", color: "#6D28D9" }}>{kw}</span>
          ))}
        </div>
      )}

      {/* 叙事正文 (dharma 模块不显示裸文，用 DharmaResultCard 代替) */}
      {narrative && routed_module !== "dharma" && (
        <div style={{ marginTop: 16, fontSize: 15, color: "#1E293B", lineHeight: 1.8, whiteSpace: "pre-wrap" }}>
          {narrative}
        </div>
      )}

      {/* 引擎引用锚点 */}
      {routed_module !== "clarify" && (
        <div style={{
          marginTop: narrative ? 10 : 0,
          padding: "4px 0",
          fontSize: 11, color: "#94A3B8",
          fontStyle: "italic",
          borderTop: narrative ? "1px solid #F1F5F9" : "none",
        }}>
          <CitationWithTooltips routed_module={routed_module} engine_facts={engine_facts} />
        </div>
      )}

      {/* 密法建议卡片 */}
      {routed_module === "dharma" && dharma_guidance && (
        <DharmaResultCard guidance={dharma_guidance} />
      )}

      {/* 化解建议 */}
      {remedies.length > 0 && (
        <div style={{ marginTop: 16, padding: "12px 14px", background: "#F0FDF4", borderRadius: 8 }}>
          <div style={{ fontSize: 12, color: "#166534", fontWeight: 600, marginBottom: 8 }}>化解建议</div>
          {remedies.map((r, i) => (
            <div key={i} style={{ fontSize: 13, color: "#15803D", marginBottom: 4 }}>
              · {r}
            </div>
          ))}
        </div>
      )}

      {/* 高级选项: 引擎事实 tooltip */}
      {engine_facts && Object.keys(engine_facts).length > 0 && (
        <details style={{ marginTop: 10 }}>
          <summary style={{ fontSize: 12, color: "#94A3B8", cursor: "pointer" }}>查看引擎计算详情</summary>
          <pre style={{ fontSize: 11, color: "#64748B", background: "#F8FAFC", padding: 10, borderRadius: 6, marginTop: 6, overflow: "auto", maxHeight: 200 }}>
            {JSON.stringify(engine_facts, null, 2)}
          </pre>
        </details>
      )}

      {/* PDF 上卖引导 */}
      {upsell?.show_pdf && (
        <div style={{ marginTop: 16, padding: "14px 16px", background: "rgba(212,175,55,.08)", border: `1px solid rgba(212,175,55,.3)`, borderRadius: 10 }}>
          <div style={{ fontSize: 13, color: GDH, fontWeight: 600, marginBottom: 6 }}>📊 发现深度分析机会</div>
          <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.6, marginBottom: 12 }}>
            {upsell.reason || "基于您的提问，系统可以为您生成完整的八字+紫微报告，包含事业、财运、流年分析。"}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={() => onNavigate("/audit")}
              style={{ background: GD, color: "#fff", border: "none", borderRadius: 7,
                padding: "8px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
            >
              了解详情
            </button>
            <button
              onClick={() => onNewQuestion && onNewQuestion()}
              style={{ background: "transparent", color: "#64748B", border: "1px solid #E2E8F0",
                borderRadius: 7, padding: "8px 14px", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}
            >
              继续免费问答
            </button>
          </div>
        </div>
      )}

      {/* 追问输入框 */}
      <FollowUpInput value={followInput} onChange={setFollowInput} onSubmit={(q) => { setFollowInput(""); onFollowUp(q); }} />

      {/* 反馈按钮 */}
      <FeedbackBar
        queryId={query_id}
        feedbackState={feedbackState}
        onFeedback={async (fb) => {
          setFeedbackState(fb);
          try {
            await fetch(`${API}/api/v4/omni/feedback`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ query_id, feedback: fb, detail: "" }),
            });
          } catch (_) {}
        }}
      />

      {/* 换引擎视角 */}
      {routed_module !== "clarify" && routed_module !== "dharma" && (
        <div style={{
          marginTop: 14, padding: "10px 14px",
          background: "rgba(249,247,242,.6)", borderRadius: 10,
          border: "1px solid rgba(212,175,55,.15)",
        }}>
          <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 8 }}>
            同一个问题 · 换个角度看
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[
              { mod: "liuyao", label: "⚡ 六爻", desc: "一卦定事" },
              { mod: "bazi",   label: "🔮 八字", desc: "格局为本" },
              { mod: "ziwei",  label: "🌟 紫微", desc: "十二宫全貌" },
            ].filter(e => e.mod !== routed_module).map(e => (
              <button
                key={e.mod}
                onClick={() => {
                  if (onFollowUp && originalQuery) {
                    onFollowUp(originalQuery, e.mod);
                  }
                }}
                style={{
                  background: "transparent", border: "1px solid #E2E8F0",
                  borderRadius: 6, padding: "5px 12px", fontSize: 12,
                  cursor: "pointer", color: "#475569",
                  display: "flex", alignItems: "center", gap: 4,
                  fontFamily: "inherit",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "#D4AF37"; e.currentTarget.style.background = "rgba(212,175,55,.06)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "#E2E8F0"; e.currentTarget.style.background = "transparent"; }}
              >
                {e.label}
                <span style={{ fontSize: 10, color: "#94A3B8" }}>{e.desc}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 深度解锁卡 — 非 dharma/clarify 模块显示 */}
      {routed_module !== "dharma" && routed_module !== "clarify" && (
        <UnlockCard routedModule={routed_module} onNavigate={onNavigate} />
      )}
    </div>
  );
}

// ─── 深度解锁卡 ───────────────────────────────────────────────────────────────
function UnlockCard({ routedModule, onNavigate }) {
  const isLiuyao = routedModule === "liuyao";
  const isDream  = routedModule === "dream";
  return (
    <div style={{
      marginTop: 18, padding: "16px 18px",
      background: "linear-gradient(135deg,rgba(212,175,55,.06),rgba(6,78,59,.04))",
      borderRadius: 12,
      border: "1px solid rgba(212,175,55,.25)",
    }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: GDH, marginBottom: 8, letterSpacing: 0.5 }}>
        🔮 想深入了解自己的能量格局？
      </div>
      <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.7, marginBottom: 12 }}>
        {isLiuyao
          ? "六爻问卜能看一时一事，八字命盘能看一生格局。了解你的日主强弱、喜用神、十神性格和大运走向。"
          : isDream
          ? "梦境是潜意识的信号，命盘是能量的地图。了解你的八字格局，理解为什么总做某种梦。"
          : "深入你的八字+紫微命盘，看见日主强弱、用神喜忌、大运流年——不只是看盘，是看清自己。"}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        {[
          { icon: "⚖️", label: "日主强弱" },
          { icon: "💧", label: "喜用神" },
          { icon: "🌟", label: "十神性格" },
          { icon: "📅", label: "大运流年" },
          { icon: "🏰", label: "紫微十二宫" },
        ].map((item, i) => (
          <span key={i} style={{
            fontSize: 11, padding: "3px 10px",
            background: "rgba(6,78,59,.06)", color: GR,
            borderRadius: 8, fontWeight: 500,
          }}>
            {item.icon} {item.label}
          </span>
        ))}
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <button
          onClick={() => onNavigate("/pricing")}
          style={{
            background: "linear-gradient(135deg,#D4AF37,#064E3B)",
            color: "#fff", border: "none", borderRadius: 8,
            padding: "9px 20px", fontSize: 13, fontWeight: 600,
            cursor: "pointer", fontFamily: "inherit", letterSpacing: ".03em",
          }}
        >
          查看定价方案 →
        </button>
        <span style={{ fontSize: 12, color: "#94A3B8", alignSelf: "center" }}>
          命盘 $39.99 · 问事 $9.99/次
        </span>
      </div>
    </div>
  );
}

// ─── 追问输入框子组件 ─────────────────────────────────────────────────────────
function FollowUpInput({ value, onChange, onSubmit }) {
  return (
    <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #F1F5F9", display: "flex", gap: 8 }}>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter" && value.trim()) { e.preventDefault(); onSubmit(value.trim()); } }}
        placeholder="追问..."
        style={{ flex: 1, border: "1.5px solid #E2E8F0", borderRadius: 8, padding: "9px 13px",
          fontSize: 14, color: "#1E293B", fontFamily: "inherit", background: "#FAFAFA" }}
      />
      <button
        onClick={() => { if (value.trim()) onSubmit(value.trim()); }}
        disabled={!value.trim()}
        style={{ background: value.trim() ? GD : "#94A3B8", color: "#fff", border: "none",
          borderRadius: 8, padding: "9px 18px", fontSize: 14, fontWeight: 600,
          cursor: value.trim() ? "pointer" : "default", fontFamily: "inherit" }}
      >
        →
      </button>
    </div>
  );
}

// ─── 共用样式 ─────────────────────────────────────────────────────────────────
const cardStyle = {
  background: "#fff",
  border: "1.5px solid rgba(212,175,55,.25)",
  borderRadius: 14,
  padding: "20px",
  boxShadow: "0 4px 24px rgba(6,78,59,.06)",
  marginTop: 16,
};

const headerStyle = {
  display: "flex",
  alignItems: "flex-start",
  gap: 12,
};

const moduleNameStyle = {
  fontSize: 15,
  fontWeight: 700,
  color: GR,
};

const explWhyStyle = {
  fontSize: 13,
  color: "#64748B",
  marginTop: 2,
  lineHeight: 1.5,
};

const factBadge = {
  fontSize: 12,
  padding: "3px 8px",
  background: "rgba(6,78,59,.07)",
  color: GR,
  borderRadius: 5,
  fontWeight: 500,
};

// ─── 反馈按钮 ─────────────────────────────────────────────────────────────────
function FeedbackBar({ queryId, feedbackState, onFeedback }) {
  if (!queryId) return null;
  return (
    <div style={{
      marginTop: 18, display: "flex", alignItems: "center", gap: 12,
      justifyContent: "center", padding: "10px 0",
    }}>
      <span style={{ fontSize: 12, color: "#94A3B8", marginRight: 4 }}>这个回复有用吗？</span>
      <button
        onClick={() => onFeedback("thumbs_up")}
        disabled={feedbackState !== null}
        style={{
          background: feedbackState === "thumbs_up" ? "rgba(5,150,105,.12)" : "transparent",
          border: `1.5px solid ${feedbackState === "thumbs_up" ? "#059669" : "#E2E8F0"}`,
          borderRadius: 8, padding: "6px 14px", cursor: feedbackState === null ? "pointer" : "default",
          fontSize: 16, lineHeight: 1,
          opacity: feedbackState !== null && feedbackState !== "thumbs_up" ? 0.4 : 1,
        }}
      >👍</button>
      <button
        onClick={() => onFeedback("thumbs_down")}
        disabled={feedbackState !== null}
        style={{
          background: feedbackState === "thumbs_down" ? "rgba(220,38,38,.10)" : "transparent",
          border: `1.5px solid ${feedbackState === "thumbs_down" ? "#DC2626" : "#E2E8F0"}`,
          borderRadius: 8, padding: "6px 14px", cursor: feedbackState === null ? "pointer" : "default",
          fontSize: 16, lineHeight: 1,
          opacity: feedbackState !== null && feedbackState !== "thumbs_down" ? 0.4 : 1,
        }}
      >👎</button>
      {feedbackState && (
        <span style={{ fontSize: 12, color: "#64748B" }}>
          已反馈{feedbackState === "thumbs_down" ? " · 我们会改进" : " · 谢谢"}
        </span>
      )}
    </div>
  );
}
